<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import questionsRaw from './../../assets/json/pickerQuestions.json';

import FeedPickerSlider from './FeedPickerSlider.vue';
import FeedPickerSearchResults from './FeedPickerSearchResults.vue';
import CustomModal from '../ui/CustomModal.vue';

import { useFeedPicker } from '../../composables/useFeedPicker';
import { getFeedPickerConfig } from '../../utils/feedPickerConfig';
import { setMagentoPageTitle } from '../../utils/magentoPageTitle';
import { resolvePickerImageUrl } from '../../utils/assetUrl';
import { fetchOptionsByFields } from '../../services/feedPickerOptionsApi';

const searchResultsRef = ref(null);

const isPopupVisible = ref(false);
const popupContent = ref('');

// текущие ответы
const currentModel = ref({});
const onModelChange = (val) => { currentModel.value = val || {}; };

// dynamic special_needs
const specialNeedsDynamic = ref([]);
const specialNeedsLoading = ref(false);
const specialNeedsCache = new Map();

const NONE_NEED = 'Пропустить';

const normalizeToArray = (value) => {
  if (Array.isArray(value)) return value.map(String);
  if (value === undefined || value === null || value === '') return [];
  return [String(value)];
};

const pickOptionsParams = (m) => {
  const pet_type = m?.pet_type;
  const pet_age = m?.pet_age;
  const pet_size = m?.pet_size || (pet_type === 'cat' ? 'cat' : undefined);

  const types = normalizeToArray(m?.types);
  const textures = normalizeToArray(m?.textures);

  const rawSterilized = m?.is_sterilized;
  const is_sterilized =
      rawSterilized === undefined || rawSterilized === null || rawSterilized === ''
          ? undefined
          : Number(rawSterilized);

  return {
    pet_type,
    pet_age,
    pet_size,
    types,
    textures,
    is_sterilized,
    brands: ['pro_plan'],
  };
};

const canRequestOptions = (p) => {
  return Boolean(
      p.pet_type &&
      p.pet_age &&
      p.pet_size &&
      Array.isArray(p.types) && p.types.length > 0
  );
};

function applyImageBaseToHtml(html) {
  if (!html) return html;

  const { imageBaseUrl } = getFeedPickerConfig();
  if (!imageBaseUrl) return html;

  // 1) src="..." / src='...'
  let out = html.replace(
      /src=(["'])(?!https?:\/\/|\/)([^"']+)\1/g,
      (_, q, path) => `src=${q}${imageBaseUrl}/${path}${q}`
  );

  // 2) src=dogs/...
  out = out.replace(
      /src=(?!["'])(?!https?:\/\/|\/)([^\s>]+)/g,
      (_, path) => `src="${imageBaseUrl}/${path}"`
  );

  return out;
}

const loadSpecialNeedsForCurrentAnswers = async (model) => {
  const params = pickOptionsParams(model);

  if (!canRequestOptions(params)) {
    specialNeedsDynamic.value = [NONE_NEED];
    return specialNeedsDynamic.value;
  }

  const key = JSON.stringify({
    pet_type: params.pet_type,
    pet_age: params.pet_age,
    pet_size: params.pet_size,
    types: [...params.types].sort(),
    textures: [...params.textures].sort(),
    is_sterilized: params.is_sterilized,
  });

  if (specialNeedsCache.has(key)) {
    specialNeedsDynamic.value = specialNeedsCache.get(key);
    return specialNeedsDynamic.value;
  }

  specialNeedsLoading.value = true;
  try {
    const data = await fetchOptionsByFields({
      fields: ['special_need'],
      params,
    });

    const list = Array.isArray(data?.special_need) ? [...data.special_need] : [];
    if (!list.includes(NONE_NEED)) list.push(NONE_NEED);

    specialNeedsCache.set(key, list);
    specialNeedsDynamic.value = list;
    return list;
  } catch (e) {
    console.error('[options] failed', e);
    specialNeedsDynamic.value = [NONE_NEED];
    return specialNeedsDynamic.value;
  } finally {
    specialNeedsLoading.value = false;
  }
};

const questionsData = computed(() => {
  const cfg = getFeedPickerConfig();

  const normalizeOption = (opt) => {
    if (!opt) return opt;

    const img = opt.img ? String(opt.img).replace(/^\/+/, '') : opt.img;

    return {
      ...opt,
      img: img ? resolvePickerImageUrl(img) : img,
    };
  };

  const mapQuestion = (q) => {
    if (!q) return q;

    if (q.value === 'special_needs') {
      return {
        ...q,
        options: (specialNeedsDynamic.value || []).map((v) => ({
          name: v,
          value: String(v).trim(),
        })),
      };
    }

    if (q.optionsSource) {
      const source = cfg[q.optionsSource] || [];
      return {
        ...q,
        options: source.map((v) =>
            normalizeOption({ name: v, value: String(v).trim() })
        ),
      };
    }

    if (Array.isArray(q.options)) {
      return { ...q, options: q.options.map(normalizeOption) };
    }

    return q;
  };

  return {
    ...questionsRaw,
    type: (questionsRaw.type || []).map(mapQuestion),
    cats: (questionsRaw.cats || []).map(mapQuestion),
    dogs: (questionsRaw.dogs || []).map(mapQuestion),
    popups: questionsRaw.popups || {},
  };
});

const {
  isSliderVisible,
  isLoading,
  error,
  defaultFilters,
  startSearch,
  applyFilters,
  availableOptions,
} = useFeedPicker();

watch(
    () => isSliderVisible.value,
    async (sliderVisible) => {
      await nextTick();
      const linkEl = document.querySelector('.feed-picker__title-link');

      if (!sliderVisible) {
        setMagentoPageTitle('Рационы бренда PRO PLAN<sup>®</sup>');
        if (linkEl) linkEl.style.display = 'block';
      } else {
        if (linkEl) linkEl.style.display = 'none';
      }
    },
    { immediate: true }
);

// popups
const showPopup = (content) => {
  popupContent.value = applyImageBaseToHtml(content);
  isPopupVisible.value = true;
};

const closePopup = () => {
  isPopupVisible.value = false;
  popupContent.value = '';
};

const FEEDPICKER_PET_TYPE_TITLES = {
  cat: 'кошки',
  dog: 'собаки',
};
const sendFeedPickerSuccessGoal = (petType) => {
  const petTypeTitle = FEEDPICKER_PET_TYPE_TITLES[petType];
  if (!petTypeTitle || typeof window === 'undefined' || typeof window.ym !== 'function') return;

  window.yaParams = {
    'Подборщик корма': {
      'Успешное прохождение': [
        petTypeTitle,
      ],
    },
  };
  window.ym(44784610, 'reachGoal', 'feedpicker_success', window.yaParams);
};

const openPopupById = (popupId) => {
  const popups = questionsData.value?.popups || {};
  const html = popups[popupId];
  if (!html) return;
  showPopup(html);
};

const onGlobalPopupClick = (event) => {
  const el = event.target?.closest?.('[data-popup-id]');
  if (!el) return;

  const popupId = el.getAttribute('data-popup-id');
  if (!popupId) return;

  event.preventDefault();
  openPopupById(popupId);
};

onMounted(() => {
  document.addEventListener('click', onGlobalPopupClick, true);
});

onUnmounted(() => {
  document.removeEventListener('click', onGlobalPopupClick, true);
});

// search handlers
const onTriggerSearch = async (sliderParams) => {
  const html = await startSearch(sliderParams);
  if (html === null) return;
  searchResultsRef.value?.updateResultsHtml(html);
  sendFeedPickerSuccessGoal(sliderParams?.pet_type);
};

const onFilterChanged = async (filters) => {
  const html = await applyFilters(filters);
  if (html === null) return;
  searchResultsRef.value?.updateResultsHtml(html);
};
</script>

<template>
  <div class="feed-picker">
    <div v-if="isLoading" class="loader" aria-live="polite" aria-busy="true">
      <svg class="loader__spinner" viewBox="0 0 50 50" role="img" aria-label="Загрузка">
        <circle class="loader__path" cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle>
      </svg>
    </div>

    <div v-if="error" class="feed-picker__error">
      <div class="feed-picker__error-text">{{ error }}</div>
    </div>

    <Transition name="fade">
      <FeedPickerSlider
          v-if="isSliderVisible && !isLoading"
          :data="questionsData"
          :popups-data="questionsData.popups"
          :load-special-needs="loadSpecialNeedsForCurrentAnswers"
          :is-special-needs-loading="specialNeedsLoading"
          @model-change="onModelChange"
          @trigger-search="onTriggerSearch"
          @show-popup="showPopup"
      />
    </Transition>

    <FeedPickerSearchResults
        v-if="!error"
        ref="searchResultsRef"
        :initial-filters="defaultFilters"
        :available-tastes="availableOptions.taste"
        :available-brands="availableOptions.brand"
        @filter-changed="onFilterChanged"
        v-show="!isSliderVisible"
    />

    <CustomModal :is-visible="isPopupVisible" title="Подробнее" @close="closePopup">
      <div v-html="popupContent"></div>
    </CustomModal>
  </div>
</template>
